import { TaxOverrideBadge } from "@/components/common/TaxOverrideBadge";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { format } from "date-fns";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { rollUpLineItems, applyCCFeeToRolledItems } from "@/lib/rollUpLineItems";

const STATUS_LABELS = { draft: "Draft", submitted: "Submitted", approved: "Approved", scheduled: "Schedule", completed: "Completed", cancelled: "Cancelled" };
const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

/* Uses shared rollUpLineItems from /lib/rollUpLineItems.js. */

export default function WOPreview({ form, clientName, siteName, siteAddress, subtotal, coSubtotal, taxAmount, total, ccFeePct = 0, ccFeeAmount = 0, editingWO, onClose }) {
  const [laborBreakout, setLaborBreakout] = useState(!!form.show_breakdown);
  const lineItems = form.line_items || [];
  const changeOrders = form.change_orders || [];
  const revisedSubtotal = subtotal + coSubtotal;
  // CC fee is silently distributed into the line rows (same as the
  // downloaded PDF, #173) so the visible rows + Subtotal footer sum to
  // the same numbers the customer sees on the download.
  const rolledUp = applyCCFeeToRolledItems(rollUpLineItems(lineItems, laborBreakout), ccFeePct, ccFeeAmount);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto py-8" onClick={onClose}>
      <div
        className="bg-white w-[816px] min-h-[600px] shadow-2xl rounded-lg"
        onClick={e => e.stopPropagation()}
        data-testid="wo-preview"
      >
        {/* Close bar */}
        <div className="sticky top-0 z-10 bg-slate-800 text-white px-6 py-2 rounded-t-lg flex justify-between items-center">
          <span className="text-sm font-medium">Work Order Preview</span>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="wo-labor-breakout-toggle">
              <input type="checkbox" checked={laborBreakout} onChange={(e) => setLaborBreakout(e.target.checked)} className="h-3.5 w-3.5 rounded accent-blue-400" />
              <span>Labor Breakout</span>
            </label>
            <Button variant="ghost" size="sm" className="text-white hover:bg-slate-700 h-7" onClick={onClose} data-testid="btn-close-wo-preview">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Page content */}
        <div className="px-12 py-10" style={{ fontFamily: "'Segoe UI', sans-serif" }}>
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 tracking-tight">WORK ORDER</h1>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono text-lg font-bold text-blue-700">{form.wo_number || "—"}</span>
                <Badge className={`text-[10px] ${
                  form.status === "completed" ? "bg-green-100 text-green-700" :
                  form.status === "approved" ? "bg-blue-100 text-blue-700" :
                  form.status === "scheduled" ? "bg-purple-100 text-purple-700" :
                  form.status === "submitted" ? "bg-yellow-100 text-yellow-700" :
                  form.status === "cancelled" ? "bg-red-100 text-red-700" :
                  "bg-slate-100 text-slate-700"
                }`}>{STATUS_LABELS[form.status] || form.status}</Badge>
              </div>
            </div>
            <div className="text-right text-xs text-slate-500 leading-relaxed">
              <div className="font-semibold text-slate-700 text-sm">{COMPANY_INFO.name}</div>
              <div>{COMPANY_INFO.fullAddress}</div>
              <div>Phone: {COMPANY_INFO.phone}</div>
              <div>Email: {COMPANY_INFO.email}</div>
            </div>
          </div>

          {/* Meta — BILL TO / PROJECT / SITE / ADDRESS */}
          <div className="grid grid-cols-2 gap-x-8 gap-y-4 text-sm mb-6">
            <div className="min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Bill To</div>
              <div className="font-medium text-slate-800 break-words">{clientName || "—"}</div>
            </div>
            <div className="text-right min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Project</div>
              <div className="font-medium text-slate-800 break-words">{form.title || "—"}</div>
            </div>
            <div className="min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Site</div>
              <div className="font-medium text-slate-700 break-words">{siteName || "—"}</div>
            </div>
            <div className="text-right min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Address</div>
              <div className="text-slate-600 break-words">{siteAddress || "—"}</div>
            </div>
            <div className="min-w-0">
              <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Date</div>
              <div className="font-medium text-slate-700">{form.work_date ? format(new Date(form.work_date + "T00:00:00"), "MMMM d, yyyy") : "—"}</div>
            </div>
            {form.scheduled_date && (
              <div className="text-right min-w-0">
                <div className="text-slate-400 text-[10px] uppercase tracking-wide font-semibold">Scheduled</div>
                <div className="font-medium text-blue-700">{format(new Date(form.scheduled_date + "T00:00:00"), "MMMM d, yyyy")}</div>
              </div>
            )}
          </div>

          {/* Qualifications (customer notes) — internal form.description is
              intentionally NOT shown: it's staff-only and never prints on
              the customer PDF, so the preview must not show it either. */}
          {form.customer_notes && (
            <div className="mb-6">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-1">Qualifications</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{form.customer_notes}</p>
            </div>
          )}

          {/* Line Items - Rolled Up Summary */}
          {rolledUp.length > 0 && (
            <div className="mb-5">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-700 text-white">
                    <th className="text-left px-2 py-1.5 font-semibold w-28">Part #</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Name</th>
                    <th className="text-center px-2 py-1.5 font-semibold w-14">Qty</th>
                    {laborBreakout && <th className="text-right px-2 py-1.5 font-semibold w-24">Material</th>}
                    {laborBreakout && <th className="text-right px-2 py-1.5 font-semibold w-24">Labor</th>}
                    <th className="text-right px-2 py-1.5 font-semibold w-28">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rolledUp.map((item, idx) => (
                    // eslint-disable-next-line react/no-array-index-key -- rolled-up line items have no stable id post-aggregation; idx is the natural row identity
                    <tr key={`woli-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className="px-2 py-1.5 font-mono text-slate-500">{item.part_number || "—"}</td>
                      <td className="px-2 py-1.5 font-medium text-slate-700">{item.name || "—"}</td>
                      <td className="px-2 py-1.5 text-center">{item.quantity}</td>
                      {laborBreakout && <td className="px-2 py-1.5 text-right tabular-nums">{fmt(item.material_total || 0)}</td>}
                      {laborBreakout && <td className="px-2 py-1.5 text-right tabular-nums">{fmt(item.labor_total || 0)}</td>}
                      <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmt(item.total)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-100 font-semibold">
                    <td colSpan={laborBreakout ? 5 : 3} className="px-2 py-1.5 text-right text-slate-600">Subtotal</td>
                    <td className="px-2 py-1.5 text-right tabular-nums font-bold">{fmt(subtotal + ccFeeAmount)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Change Orders */}
          {changeOrders.length > 0 && (
            <div className="mb-5">
              <h3 className="text-sm font-bold text-slate-800 mb-2 uppercase tracking-wide">Change Orders</h3>
              {changeOrders.map((co) => {
                const coLineItems = co.line_items || [];
                const coRolled = rollUpLineItems(coLineItems, laborBreakout);
                const coSub = co.subtotal != null
                  ? co.subtotal
                  : coLineItems.reduce((s, li) => s + (li.quantity || 0) * (li.unit_price || 0), 0);
                // Material-only subtotal for tax calc — tax applies only to material (cost_type 2/material)
                const coMaterialSub = coLineItems.reduce((s, li) => {
                  const ct = String(li.cost_type || "").toLowerCase();
                  if (ct === "2" || ct === "material") return s + (li.quantity || 0) * (li.unit_price || 0);
                  return s;
                }, 0);
                const coOv = co.tax_override || null;
                const coTaxRate = coOv?.type === "rate" ? (parseFloat(coOv.rate) || 0) : (parseFloat(form.tax_rate) || 0);
                const coTaxAmt = coOv?.type === "amount" ? (parseFloat(coOv.amount) || 0) : coMaterialSub * (coTaxRate / 100);
                const coGrand = coSub + coTaxAmt;
                const showTaxLine = (coTaxRate > 0 && coMaterialSub > 0) || !!coOv;
                return (
                  <div key={co.id} className="mb-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">{co.co_number}</span>
                      <span className="text-xs text-slate-600">{co.description || ""}</span>
                    </div>
                    <table className="w-full text-xs border-collapse">
                      <thead>
                        <tr className="bg-amber-700 text-white">
                          <th className="text-left px-2 py-1.5 font-semibold w-28">Part #</th>
                          <th className="text-left px-2 py-1.5 font-semibold">Name</th>
                          <th className="text-center px-2 py-1.5 font-semibold w-14">Qty</th>
                          {laborBreakout && <th className="text-right px-2 py-1.5 font-semibold w-24">Material</th>}
                          {laborBreakout && <th className="text-right px-2 py-1.5 font-semibold w-24">Labor</th>}
                          <th className="text-right px-2 py-1.5 font-semibold w-28">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {coRolled.map((item, liIdx) => (
                          // eslint-disable-next-line react/no-array-index-key -- CO rolled-up line items have no stable id after aggregation
                          <tr key={`coli-${liIdx}`} className={liIdx % 2 === 0 ? "bg-white" : "bg-amber-50/30"}>
                            <td className="px-2 py-1.5 font-mono text-slate-500">{item.part_number || "—"}</td>
                            <td className="px-2 py-1.5 font-medium text-slate-700">{item.name || "—"}</td>
                            <td className="px-2 py-1.5 text-center">{item.quantity}</td>
                            {laborBreakout && <td className="px-2 py-1.5 text-right tabular-nums">{fmt(item.material_total || 0)}</td>}
                            {laborBreakout && <td className="px-2 py-1.5 text-right tabular-nums">{fmt(item.labor_total || 0)}</td>}
                            <td className="px-2 py-1.5 text-right tabular-nums font-medium">{fmt(item.total)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-amber-50 font-medium text-amber-900">
                          <td colSpan={laborBreakout ? 5 : 3} className="px-2 py-1 text-right">CO Subtotal</td>
                          <td className="px-2 py-1 text-right tabular-nums">{fmt(coSub)}</td>
                        </tr>
                        {showTaxLine && (
                          <tr className="bg-amber-50 text-amber-900">
                            <td colSpan={laborBreakout ? 5 : 3} className="px-2 py-1 text-right">{coOv?.type === "amount" ? "Tax" : `Tax (${coTaxRate}% on material)`}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{fmt(coTaxAmt)}</td>
                          </tr>
                        )}
                        <tr className="bg-amber-100/50 font-semibold">
                          <td colSpan={laborBreakout ? 5 : 3} className="px-2 py-1 text-right text-amber-800">CO Total</td>
                          <td className="px-2 py-1 text-right tabular-nums font-bold text-amber-800">{fmt(coGrand)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                );
              })}
            </div>
          )}

          {/* Grand Totals */}
          <div className="flex justify-end mt-4">
            <div className="w-64 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500">
                <span>Original Subtotal</span>
                <span className="tabular-nums font-medium">{fmt(subtotal + ccFeeAmount)}</span>
              </div>
              {coSubtotal > 0 && (
                <>
                  <div className="flex justify-between text-amber-700">
                    <span>Change Orders</span>
                    <span className="tabular-nums font-medium">+{fmt(coSubtotal)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>Revised Subtotal</span>
                    <span className="tabular-nums">{fmt(revisedSubtotal + ccFeeAmount)}</span>
                  </div>
                </>
              )}
              {((form.tax_rate > 0 && taxAmount > 0) || form.tax_override || (form.change_orders || []).some(co => co.status === "approved" && co.tax_override)) && (
                <div className="flex justify-between text-slate-500">
                  <span>{form.tax_override?.type === "amount" ? "Tax" : `Tax (${form.tax_rate}% on material)`}</span>
                  <span className="tabular-nums font-medium">{fmt(taxAmount)}</span>
                </div>
              )}
              {form.tax_override && (
                <div className="flex justify-end print:hidden" data-testid="wo-preview-tax-override">
                  <TaxOverrideBadge override={form.tax_override} canManage={false} testId="wo-preview-tax-badge" />
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-slate-300 text-lg font-bold text-slate-900">
                <span>Total</span>
                <span className="tabular-nums">{fmt(total)}</span>
              </div>
            </div>
          </div>

          {/* Equipment Inspection — mirrors the download PDF */}
          {(form.inspection_items || []).length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Equipment Inspection</h3>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-700 text-white">
                    <th className="text-left px-2 py-1.5 font-semibold">Equipment</th>
                    <th className="text-left px-2 py-1.5 font-semibold w-28">Status</th>
                    <th className="text-left px-2 py-1.5 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {form.inspection_items.map((it, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={`insp-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                      <td className="px-2 py-1.5 font-medium text-slate-700">{it.equipment_name}</td>
                      <td className="px-2 py-1.5 text-slate-600 capitalize">{it.status}</td>
                      <td className="px-2 py-1.5 text-slate-500">{it.notes || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Signature */}
          {editingWO?.signature_data && (
            <div className="mt-8 border-t pt-6">
              <h3 className="text-sm font-bold uppercase tracking-wide text-slate-700 mb-2">Customer Signature</h3>
              <img src={editingWO.signature_data} alt="Signature" className="h-20 border rounded" />
              <p className="text-xs text-slate-500 mt-1">
                Signed by: {editingWO.signed_by || "N/A"}
                {editingWO.signed_at ? ` on ${format(new Date(editingWO.signed_at), "MMM d, yyyy h:mm a")}` : ""}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
